/**
 * A labelled input.
 *
 * The label is a real <label> tied to the input, so tapping it focuses the
 * field and a screen reader announces the two together. An error is tied to
 * the input as well, so it is read out rather than only seen.
 */
import { useId, useState } from 'react';

export function Field({ label, error, hint, className = '', ...props }) {
  const id = useId();

  /**
   * A password people can check before they commit to it.
   *
   * WHY IT IS WORTH THE BUTTON
   * --------------------------
   * Every password here is typed on a device somebody else set up, from a
   * value read down a telephone or off a slip of paper — a rider's temporary
   * password, a new cashier's first sign-in. Get one character wrong and the
   * answer is "invalid credentials", which is indistinguishable from having
   * the wrong password entirely.
   *
   * Five of those in a row locks the account for half an hour. Being able to
   * look at what you typed is the difference between a second attempt and a
   * telephone call.
   *
   * It reveals only while held open, and starts hidden, so a password is never
   * left on screen in a room with customers in it.
   */
  const [revealed, setRevealed] = useState(false);
  const isPassword = props.type === 'password';
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-ink-2">
        {label}
      </label>

      <div className="relative">
        <input
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : hint ? hintId : undefined}
          className={`
            h-12 w-full rounded-lg border bg-surface-3 px-3.5 text-[15px] text-ink
            placeholder:text-ink-3 focus:outline-none focus:ring-2
            ${isPassword ? 'pr-12' : ''}
            ${
              error
                ? 'border-bad focus:border-bad focus:ring-bad/30'
                : 'border-rule focus:border-brand-400 focus:ring-brand-400/30'
            }
          `}
          {...props}
          type={isPassword && revealed ? 'text' : props.type}
        />

        {isPassword && (
          <button
            type="button"
            onClick={() => setRevealed((was) => !was)}
            aria-label={revealed ? 'Hide the password' : 'Show the password'}
            aria-pressed={revealed}
            className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-lg text-ink-3 hover:text-ink focus:outline-none focus:ring-2 focus:ring-brand-400/30"
          >
            {revealed ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
          </button>
        )}
      </div>

      {error ? (
        <p id={errorId} role="alert" className="mt-1.5 text-sm text-bad">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="mt-1.5 text-sm text-ink-3">
          {hint}
        </p>
      ) : null}
    </div>
  );
}


/**
 * Drawn here rather than pulled from an icon set.
 *
 * Two shapes, twenty lines, and no dependency on a package for something this
 * small — the alternative brings a few hundred kilobytes of icons to show one.
 */
function Eye(props) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" {...props}>
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOff(props) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" {...props}>
      <path d="M2 12s3.6-7 10-7c1.7 0 3.2.5 4.5 1.2M22 12s-3.6 7-10 7c-1.7 0-3.2-.5-4.5-1.2" />
      <path d="M3 3l18 18" />
    </svg>
  );
}
